import { apiClient } from '../client';

/**
 * Client for `/api/eos/rocks/*`. Stays in lockstep with
 * `server/src/routes/eos/rocks.routes.ts` (Stream B Wave 1, ADR-023).
 */

export type RockType = 'company' | 'department' | 'individual';
export type RockStatus = 'on_track' | 'off_track' | 'done';

/**
 * Approval lifecycle (Phase H — Rocks workflow). A rock is authored as a
 * `draft`, `submitted` moves it to `pending_approval` (awaiting the slots in
 * `pendingApprovers`), and once every required slot approves it becomes
 * `active`. A rejection sends it back to `draft` with a reason.
 */
export type RockApprovalState = 'draft' | 'pending_approval' | 'active';
export type RockApprover = 'integrator' | 'manager';

export interface RockMilestone {
  id: string;
  rockId: string;
  organizationId: string;
  title: string;
  dueDate: string | null;
  completedAt: string | null;
  /** Health signal. Completing a milestone auto-sets this to `done` server-side. */
  status: RockStatus;
  order: number;
  createdAt: string;
  updatedAt: string;
}

/** A prerequisite rock this rock waits on (Phase H — Rocks depth). */
export interface RockDependency {
  id: string;
  dependsOnRockId: string;
  dependsOnRock: {
    id: string;
    title: string;
    status: RockStatus;
    quarterStart: string;
  };
}

export interface Rock {
  id: string;
  organizationId: string;
  title: string;
  description: string | null;
  type: RockType;
  ownerMembershipId: string | null;
  /** Owner display name, included server-side (directory is self-only
   *  for plain employees but EOS accountability is org-transparent). */
  ownerMembership?: { id: string; user: { firstName: string; lastName: string } } | null;
  departmentId: string | null;
  /** ISO datetime of the UTC quarter boundary (Jan/Apr/Jul/Oct 1st). */
  quarterStart: string;
  status: RockStatus;
  completedAt: string | null;
  order: number;
  createdByMembershipId: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  milestones: RockMilestone[];
  /** True when any prerequisite rock is not yet `done`. Server-computed. */
  blocked: boolean;
  /** Count of open milestones flagged `off_track`. Server-computed. */
  offTrackMilestoneCount: number;
  /** Prerequisite rocks this rock is waiting on. */
  dependencies: RockDependency[];

  // ── Approval lifecycle (Phase H — Rocks workflow) ─────────────────────────
  approvalState: RockApprovalState;
  submittedAt: string | null;
  integratorApprovedAt: string | null;
  managerApprovedAt: string | null;
  activatedAt: string | null;
  rejectedAt: string | null;
  rejectionReason: string | null;
  /** Approval slots still outstanding; `[]` when active or draft. */
  pendingApprovers: RockApprover[];
  /** Whether THIS viewer may approve/reject this rock right now (server-computed
   *  against the actual approvers). The list route sets it; other responses
   *  default false and the UI re-reads from the refetched list. */
  viewerCanApprove: boolean;
}

export interface ListRocksQuery {
  /** YYYY-MM-DD quarter boundary — required by the server. */
  quarterStart: string;
  departmentId?: string;
  type?: RockType;
  ownerMembershipId?: string;
  includeArchived?: boolean;
}

export async function listRocks(q: ListRocksQuery): Promise<Rock[]> {
  const params = new URLSearchParams({ quarterStart: q.quarterStart });
  if (q.departmentId) params.set('departmentId', q.departmentId);
  if (q.type) params.set('type', q.type);
  if (q.ownerMembershipId) params.set('ownerMembershipId', q.ownerMembershipId);
  if (q.includeArchived) params.set('includeArchived', 'true');
  const res = await apiClient.get<{ data: Rock[] }>(`/api/eos/rocks?${params.toString()}`);
  return res.data.data;
}

export interface RocksRollup {
  byStatusType: { status: RockStatus; type: RockType; count: number }[];
  byDepartment: { departmentId: string | null; status: RockStatus; count: number }[];
}

export async function getRocksRollup(quarterStart: string): Promise<RocksRollup> {
  const params = new URLSearchParams({ quarterStart });
  const res = await apiClient.get<{ data: RocksRollup }>(`/api/eos/rocks/rollup?${params.toString()}`);
  return res.data.data;
}

export interface CreateRockRequest {
  title: string;
  description?: string;
  type?: RockType;
  ownerMembershipId?: string;
  departmentId?: string;
  /** YYYY-MM-DD quarter boundary. */
  quarterStart: string;
  status?: RockStatus;
  order?: number;
}

export async function createRock(body: CreateRockRequest): Promise<Rock> {
  const res = await apiClient.post<{ data: Rock }>('/api/eos/rocks', body);
  return res.data.data;
}

export interface UpdateRockRequest {
  title?: string;
  description?: string | null;
  type?: RockType;
  ownerMembershipId?: string | null;
  departmentId?: string | null;
  quarterStart?: string;
  status?: RockStatus;
  /** ISO datetime with offset, or `null` to clear. */
  completedAt?: string | null;
  order?: number;
}

export async function updateRock(id: string, body: UpdateRockRequest): Promise<Rock> {
  const res = await apiClient.put<{ data: Rock }>(`/api/eos/rocks/${encodeURIComponent(id)}`, body);
  return res.data.data;
}

/** Soft delete — sets `archivedAt`; history stays queryable. */
export async function archiveRock(id: string): Promise<void> {
  await apiClient.delete(`/api/eos/rocks/${encodeURIComponent(id)}`);
}

export interface CreateMilestoneRequest {
  title: string;
  /** ISO datetime with offset, or `null`. */
  dueDate?: string | null;
  order?: number;
}

export async function createMilestone(rockId: string, body: CreateMilestoneRequest): Promise<RockMilestone> {
  const res = await apiClient.post<{ data: RockMilestone }>(
    `/api/eos/rocks/${encodeURIComponent(rockId)}/milestones`,
    body,
  );
  return res.data.data;
}

export interface UpdateMilestoneRequest {
  title?: string;
  dueDate?: string | null;
  /** ISO datetime with offset to complete, `null` to reopen. */
  completedAt?: string | null;
  /** Health toggle for an open milestone. Completing auto-sets `done` server-side. */
  status?: RockStatus;
  order?: number;
}

export async function updateMilestone(
  rockId: string,
  milestoneId: string,
  body: UpdateMilestoneRequest,
): Promise<RockMilestone> {
  const res = await apiClient.put<{ data: RockMilestone }>(
    `/api/eos/rocks/${encodeURIComponent(rockId)}/milestones/${encodeURIComponent(milestoneId)}`,
    body,
  );
  return res.data.data;
}

export async function deleteMilestone(rockId: string, milestoneId: string): Promise<void> {
  await apiClient.delete(
    `/api/eos/rocks/${encodeURIComponent(rockId)}/milestones/${encodeURIComponent(milestoneId)}`,
  );
}

// ── Dependencies (Phase H — Rocks depth) ───────────────────────────────────

/** Add a prerequisite: this rock depends on `dependsOnRockId` finishing first. */
export async function createRockDependency(
  rockId: string,
  body: { dependsOnRockId: string },
): Promise<RockDependency> {
  const res = await apiClient.post<{ data: RockDependency }>(
    `/api/eos/rocks/${encodeURIComponent(rockId)}/dependencies`,
    body,
  );
  return res.data.data;
}

export async function deleteRockDependency(rockId: string, dependencyId: string): Promise<void> {
  await apiClient.delete(
    `/api/eos/rocks/${encodeURIComponent(rockId)}/dependencies/${encodeURIComponent(dependencyId)}`,
  );
}

// ── Approval lifecycle (Phase H — Rocks workflow) ───────────────────────────

/** Move a `draft` rock into `pending_approval`. Server error: `not_draft`. */
export async function submitRock(id: string): Promise<Rock> {
  const res = await apiClient.post<{ data: Rock }>(`/api/eos/rocks/${encodeURIComponent(id)}/submit`, {});
  return res.data.data;
}

/**
 * Record the caller's approval for a `pending_approval` rock. Once every
 * required slot has approved the server flips it to `active`. Server errors:
 * `not_pending`, `not_an_approver`.
 */
export async function approveRock(id: string): Promise<Rock> {
  const res = await apiClient.post<{ data: Rock }>(`/api/eos/rocks/${encodeURIComponent(id)}/approve`, {});
  return res.data.data;
}

/**
 * Reject a `pending_approval` rock, sending it back to `draft` with an
 * optional reason. Server errors: `not_pending`, `not_an_approver`.
 */
export async function rejectRock(id: string, body: { reason?: string }): Promise<Rock> {
  const res = await apiClient.post<{ data: Rock }>(`/api/eos/rocks/${encodeURIComponent(id)}/reject`, body);
  return res.data.data;
}
